<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { setLocale, SUPPORTED_LOCALES, type Locale } from '@/i18n';

const auth = useAuthStore();
const { t, locale } = useI18n();

const navItems = computed(() => [
  { to: '/calendar',  labelKey: 'nav.calendar',    icon: 'pi-calendar' },
  { to: '/my-leaves', labelKey: 'nav.myLeaves',    icon: 'pi-bookmark' },
  { to: '/team',      labelKey: 'nav.team',        icon: 'pi-users' },
  { to: '/heatmap',   labelKey: 'nav.heatmap',     icon: 'pi-th-large' },
  ...(auth.user?.isAdmin
    ? [
        { to: '/admin/users',       labelKey: 'nav.users',       icon: 'pi-user' },
        { to: '/admin/departments', labelKey: 'nav.departments', icon: 'pi-sitemap' },
      ]
    : []),
  { to: '/feeds',     labelKey: 'nav.feeds',       icon: 'pi-share-alt' },
  ...(auth.user?.isAdmin
    ? [{ to: '/settings', labelKey: 'nav.settings', icon: 'pi-cog' }]
    : []),
]);

function onLocaleChange(e: Event) {
  setLocale((e.target as HTMLSelectElement).value as Locale);
}
</script>

<template>
  <div class="app-shell">
    <aside class="sidebar">
      <div class="brand">
        <svg class="brand-mark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 195 98" fill="currentColor" role="img" aria-label="Zadig & Voltaire">
          <mask id="zv-logo-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="195" height="98"><path d="m0 0h194.87v97.5h-194.87z" fill="#fff"/></mask>
          <g mask="url(#zv-logo-mask)"><path d="m194.87 0h-38.45v3.61c11.69.72 13.14 6.87 8.8 16.87l-21.93 50.98-22.18-50.74c-3.86-8.8-3.37-17.11 10.36-17.11v-3.61h-44.23v3.61c10.12.6 12.53 5.30002 15.91 12.89l36.15 80.99h2.41l37.72-79.78c4.46-9.39999 5.9-13.14 15.43-14.1v-3.61zm-121.72 65.44c-3.61 18.32-10.12 21.81-35.55 22.42l-15.43.36 53.87-87.619994-.24-.600006h-70.86l-1.33001 27.12h3.86001c2.29-14.1 6.63-18.56001 26.88-18.68001l19.89-.12-54.23002075 87.49001.60000575.6h74.240015l2.17-30.97h-3.86z"/></g>
        </svg>
        <h1 class="brand-title">TIMEOFF</h1>
        <img src="/timeoff-icon.png" alt="" class="brand-icon" />
      </div>

      <nav>
        <RouterLink v-for="item in navItems" :key="item.to" :to="item.to" class="nav-item">
          <i :class="['pi', item.icon]" />
          <span>{{ t(item.labelKey) }}</span>
        </RouterLink>
      </nav>

      <div v-if="auth.user" class="user">
        <div class="user-avatar">{{ auth.user.firstName[0] }}{{ auth.user.lastName[0] }}</div>
        <div class="user-meta">
          <div class="user-name">{{ auth.user.firstName }} {{ auth.user.lastName }}</div>
          <div class="user-email">{{ auth.user.email }}</div>
        </div>
        <select class="locale-pick" :value="locale" @change="onLocaleChange" :title="locale">
          <option v-for="l in SUPPORTED_LOCALES" :key="l" :value="l">{{ l.toUpperCase() }}</option>
        </select>
        <button class="logout-btn" @click="auth.logout" :title="t('common.signOut')" :aria-label="t('common.signOut')">
          <i class="pi pi-sign-out" />
        </button>
      </div>
    </aside>

    <main class="content">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.app-shell {
  display: grid;
  /* minmax(0, 1fr) is critical: plain `1fr` would resolve to `minmax(auto, 1fr)`
     and the right column would grow past the viewport when a child has
     min-width: max-content (e.g. the wide Team timeline) — pushing the
     sidebar off-screen. */
  grid-template-columns: 240px minmax(0, 1fr);
  min-height: 100vh;
  /* `clip` instead of `hidden`: `overflow-x: hidden` silently promotes
     overflow-y to `auto`, turning .app-shell into the scroll container —
     which makes the sticky sidebar stick inside it rather than the
     viewport, leaving a gap under the sidebar when the page scrolls.
     `clip` hides overflow without creating a scrolling context. */
  overflow-x: clip;
  /* Paint the sidebar's background on the grid track itself. The sidebar
     is `position: sticky; height: 100vh` (so it stays glued to the
     viewport during scroll), which means its OWN box only fills 100vh
     even when the page is taller. Without this gradient, pages taller
     than the viewport would show the page's ivory background under the
     sidebar's bottom edge. The gradient extends to the full document
     height for free. */
  background:
    linear-gradient(
      to right,
      var(--surface-container-lowest) 0,
      var(--surface-container-lowest) 240px,
      var(--surface-container-highest) 240px,
      var(--surface-container-highest) 241px,
      var(--surface) 241px
    );
}

.sidebar {
  position: sticky;
  top: 0;
  align-self: start;
  height: 100vh;
  border-right: 1px solid var(--surface-container-highest);
  padding: 32px 0 16px;
  display: flex;
  flex-direction: column;
}

.brand {
  padding: 0 24px 48px;
  display: flex; align-items: center; gap: 8px;
  color: var(--primary);
}
/* Z&V wordmark — same SVG used by Tower's main nav. `currentColor` lets it
   inherit `.brand`'s color so it picks up the ink palette automatically. */
.brand-mark {
  height: 18px; width: auto;
  display: block;
  flex-shrink: 0;
}
.brand-title {
  border-left: 1px solid #71717a;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1;
  color: #71717a;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: .12em;
  padding-left: 8px;
  text-transform: uppercase;
}
.brand-icon {
  width: 18px; height: 18px;
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

nav { display: flex; flex-direction: column; }
.nav-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 24px;
  color: var(--on-surface);
  font-family: 'Cormorant Garamond', serif;
  font-size: 15px; font-weight: 500;
  border-left: 3px solid transparent;
  transition: color 0.15s, background-color 0.15s, border-color 0.15s;
  outline: none;
}
.nav-item:hover { color: var(--primary); background: var(--surface-container-low); }
.nav-item:focus-visible { background: var(--surface-container-low); }
.nav-item.router-link-active {
  color: var(--primary);
  background: var(--surface-container);
  border-left-color: var(--primary);
  font-weight: 600;
  padding-left: 21px;
}
.nav-item .pi { font-size: 15px; color: var(--on-surface-variant); }
.nav-item:hover .pi { color: var(--primary); }
.nav-item.router-link-active .pi { color: var(--primary); }

/* `margin-top: auto` pushes the user block to the bottom of the flex
   column. The sidebar itself is `position: sticky; height: 100vh; display:
   flex; flex-direction: column`, so this anchors the user card to the
   viewport's bottom edge regardless of how short or long the page is. */
.user {
  margin-top: auto;
  padding: 16px 24px;
  border-top: 1px solid var(--surface-container-highest);
  display: flex; align-items: center; gap: 10px;
}
.user-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: var(--primary); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Cormorant Garamond', serif; font-size: 12px; font-weight: 500;
  letter-spacing: 0.05em; flex-shrink: 0;
}
.user-meta { min-width: 0; flex: 1; }
.user-name { font-size: 13px; font-weight: 500; color: var(--primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.2; }
.user-email { font-size: 10px; color: var(--outline); letter-spacing: 0.02em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.logout-btn {
  width: 28px; height: 28px;
  background: transparent;
  color: var(--on-surface-variant);
  border: 1px solid var(--surface-container-highest);
  border-radius: var(--radius-sm);
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: color 0.15s, border-color 0.15s;
}
.logout-btn:hover { color: var(--secondary); border-color: var(--secondary); }
/* Compact locale picker — tucked beside the logout button. We use a native
   <select> for accessibility and zero JS overhead; the visible chrome is
   the 2-letter language code in caps. */
.locale-pick {
  appearance: none; -webkit-appearance: none;
  background: transparent;
  color: var(--on-surface-variant);
  border: 1px solid var(--surface-container-highest);
  border-radius: var(--radius-sm);
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.08em;
  padding: 6px 4px;
  width: 36px; height: 28px;
  cursor: pointer;
  text-align: center;
  outline: none;
  flex-shrink: 0;
  transition: color 0.15s, border-color 0.15s;
}
.locale-pick:hover, .locale-pick:focus-visible { color: var(--primary); border-color: var(--primary); }
.logout-btn .pi { font-size: 12px; }
.logout-btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

.content {
  background: var(--surface);
  min-width: 0;
  padding: 40px;
}
</style>
